import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, CheckCircle2, AlertTriangle, FileText, Mic, CreditCard, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

const InterviewCountdown = ({ interviewDate, caseData }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  
  useEffect(() => {
    if (!interviewDate) return;
    
    const calculateTimeLeft = () => {
      const interview = new Date(interviewDate);
      const now = new Date();
      const diff = interview - now;
      
      if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, isPast: true };
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      return { days, hours, minutes, isPast: false };
    };
    
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, [interviewDate]);
  
  if (!interviewDate || !timeLeft) return null;
  
  // Checklist de tareas antes de la entrevista
  const preInterviewTasks = [
    {
      id: "documents",
      label: "Documentos completos",
      completed: (caseData?.documents_count || 0) >= 3,
      icon: FileText,
      link: "/step/documents"
    },
    {
      id: "ds160",
      label: "DS-160 completado",
      completed: (caseData?.ds160_progress || 0) >= 12,
      icon: FileText,
      link: "/step/ds160"
    },
    {
      id: "practice",
      label: "Simulación de entrevista",
      completed: (caseData?.simulations_count || 0) >= 1,
      icon: Mic,
      link: "/step/interview-prep"
    },
    {
      id: "mrv",
      label: "Pago MRV confirmado",
      completed: caseData?.mrv_paid || false,
      icon: CreditCard,
      link: "/step/mrv"
    }
  ];
  
  const completedTasks = preInterviewTasks.filter(t => t.completed).length;
  const allTasksCompleted = completedTasks === preInterviewTasks.length;
  
  // Determinar urgencia basada en días restantes
  const getUrgencyColor = () => {
    if (timeLeft.isPast) return "from-gray-600 to-gray-700";
    if (timeLeft.days <= 3) return "from-red-500 to-red-600";
    if (timeLeft.days <= 7) return "from-amber-500 to-amber-600";
    return "from-blue-500 to-blue-600";
  };
  
  const getUrgencyText = () => {
    if (timeLeft.isPast) return "¡Tu entrevista ya pasó!";
    if (timeLeft.days <= 3) return "¡Tu entrevista es muy pronto!";
    if (timeLeft.days <= 7) return "Tu entrevista se acerca";
    return "Countdown para tu entrevista";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl p-6 bg-gradient-to-r ${getUrgencyColor()} text-white shadow-xl overflow-hidden relative`}
      data-testid="interview-countdown"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <p className="text-white/80 text-sm">{getUrgencyText()}</p>
              <p className="font-bold text-lg">
                {new Date(interviewDate).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
          
          {!timeLeft.isPast && (
            <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">Embajada</span>
            </div>
          )}
        </div>
        
        {/* Countdown */}
        {!timeLeft.isPast && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white/20 rounded-xl p-4 text-center">
              <p className="text-4xl font-bold">{timeLeft.days}</p>
              <p className="text-white/80 text-sm">días</p>
            </div>
            <div className="bg-white/20 rounded-xl p-4 text-center">
              <p className="text-4xl font-bold">{timeLeft.hours}</p>
              <p className="text-white/80 text-sm">horas</p>
            </div>
            <div className="bg-white/20 rounded-xl p-4 text-center">
              <p className="text-4xl font-bold">{timeLeft.minutes}</p>
              <p className="text-white/80 text-sm">minutos</p>
            </div>
          </div>
        )}
        
        {/* Pre-interview checklist */}
        <div className="bg-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-medium flex items-center gap-2">
              {allTasksCompleted ? (
                <CheckCircle2 className="w-5 h-5 text-green-300" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-300" />
              )}
              Checklist pre-entrevista
            </p>
            <span className="text-sm bg-white/20 px-2 py-1 rounded-full">
              {completedTasks}/{preInterviewTasks.length}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {preInterviewTasks.map((task) => {
              const Icon = task.icon;
              return (
                <Link
                  key={task.id}
                  to={task.link}
                  className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                    task.completed 
                      ? "bg-green-500/30 text-green-100" 
                      : "bg-white/10 hover:bg-white/20"
                  }`}
                >
                  {task.completed ? (
                    <CheckCircle2 className="w-4 h-4 text-green-300" />
                  ) : (
                    <Icon className="w-4 h-4 text-white/60" />
                  )}
                  <span className="text-sm truncate">{task.label}</span>
                </Link>
              );
            })}
          </div>
          
          {!allTasksCompleted && !timeLeft.isPast && (
            <p className="text-center text-yellow-200 text-sm mt-3">
              ⚠️ Completa todas las tareas antes de tu entrevista
            </p>
          )}
          
          {allTasksCompleted && !timeLeft.isPast && (
            <p className="text-center text-green-200 text-sm mt-3">
              ✨ ¡Estás listo para tu entrevista!
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default InterviewCountdown;
